"use strict";
// ================================================================
// v2/tokens/colors.ts
// Source of truth: Figma "App-Design" 変数 (light を正とする / dark は派生)
// Screen origin: EventDetail (UT-27465) / 後続 LotteryDetail・ArtistTop で再利用
//
// 構造はデザイナーのセマンティックトークン体系に準拠:
//   background / foreground / border / unique / shadow(色)
// 値はモード依存。box-shadow 文字列は theme/createV2Palette.ts で合成する。
//
// Figma 変数対応表 (light 値。⇄ 右が Figma 変数名):
//   background.base            ⇄ Background/neutral/subtle  (#f7f9fa)
//   background.sub             ⇄ Background/sub<new>        (#ffffff8c)
//   background.surface         ⇄ Background/surface<new>    (#ffffffcc)
//   background.neutral.default ⇄ Background/neutral/default (#e5ecef)
//   background.info.subtle     ⇄ Background/info/subtle     (#e2f1fe)
//   background.warning.subtle  ⇄ Background/warning/subtle  (#efdcb2)
//     ※Figma には typo の Background/waring/subtle も残っており、EventDetail は
//       そちらを bind している。dark 値は正しい綴りの側にしか入っていないため、
//       こちらは Background/warning/subtle を正とする
//   foreground.neutral.primary   ⇄ Foreground/neutral/primary   (#20343f)
//   foreground.neutral.secondary ⇄ Foreground/neutral/secondary (#798a94)
//   foreground.neutral.hint      ⇄ Foreground/neutral/hint      (#a7b8c0)
//   foreground.neutral.disabled  ⇄ Foreground/neutral/disabled  (#c1d0d7)
//   foreground.neutral.white     ⇄ Foreground/neutral/white     (#ffffff)
//   foreground.primary.main      ⇄ Foreground/primary/main      (#1970d4)
//   foreground.semantic.info     ⇄ Foreground/semantic/info     (#1990f6)
//   foreground.semantic.warning  ⇄ Foreground/semantic/warning  (#d36500)
//   border.neutral.default     ⇄ Border/neutral/default     (#e5ecef)
//   border.neutral.strong      ⇄ Border/neutral/strong      (#d2dee2)
//   shadowColor.card           ⇄ Effect/Shadow/Card         (#1212120f)
//   shadowColor.innerCard      ⇄ Effect/Shadow/InnerCard    (#ffffff80)
//   shadowColor.brand          ⇄ Effect/Shadow/Brand        (#1970d452)
//   合成: shadow.cardGlass ⇄ Card / shadow.card ⇄ CardShadow / shadow.brand ⇄ BrandShadow
//   ※ overlay* / topScrim* / secondary / error / unique / *.subtle(primary) 等は
//     Figma 未取得 or 独自定義 (topScrim* はデザイナーからの Slack 指定)
// ================================================================
Object.defineProperty(exports, "__esModule", { value: true });
exports.v2ColorsByMode = exports.darkColors = exports.lightColors = void 0;
// ── Light ─────────────────────────────────────────────────────────
exports.lightColors = {
    background: {
        overlay: '#00000066',
        overlayPrimary: '#459CFF33',
        overlayHover: '#0000000D',
        base: '#F7F9FA',
        sub: '#FFFFFF8C',
        surface: '#FFFFFFCC',
        topScrimFrom: '#FFFFFFA6',
        topScrimTo: '#FFFFFF00',
        neutral: { default: '#E5ECEF', subtle: '#F7F9FA' },
        secondary: { default: '#27CFCF', subtle: '#E0F7F7' },
        info: { default: '#1990F6', subtle: '#E2F1FE' },
        error: { default: '#D32F2F', subtle: '#FFCDD2' },
        warning: { default: '#D36500', subtle: '#EFDCB2' },
        primary: { default: '#1970D4', subtle: '#F1F8FF' },
    },
    foreground: {
        neutral: {
            primary: '#20343F',
            secondary: '#798A94',
            hint: '#A7B8C0',
            disabled: '#C1D0D7',
            white: '#FFFFFF',
            black: '#000000',
        },
        primary: { main: '#1970D4', activated: '#D2DEE2', onPrimary: '#FFFFFF' },
        semantic: {
            info: '#1990F6',
            error: '#D32F2F',
            success: '#27CFCF',
            warning: '#D36500',
        },
        filledInk: { light: '#FFFFFF', dark: '#20343F' },
    },
    border: {
        neutral: { default: '#E5ECEF', strong: '#D2DEE2' },
        semantic: {
            info: '#1990F6',
            error: '#D32F2F',
            success: '#27CFCF',
            warning: '#D36500',
        },
    },
    unique: { protect: '#1640A1', nft: '#27CFCF', resale: '#1970D4' },
    shadowColor: {
        card: '#1212120F',
        innerCard: '#FFFFFF80',
        brand: '#1970D452',
    },
};
// ── Dark ──────────────────────────────────────────────────────────
//
// Figma "Color" コレクションの Dark Mode をそのまま写している。ただし Figma 側で
// dark 値が未整備 (light と同値のまま) のトークンがあり、そのまま使うと暗い面に
// 明るい面色・低コントラストの文字色が乗るため、こちらで派生させている。
// 派生ルールは Figma が dark を整備済みのトークンに合わせる:
//   面色 (*.subtle)      … 同色相のまま明度を下げる (info.subtle #16344D 相当の明度帯)
//   文字色・境界線 (semantic) … background.<同名>.default の dark 値と揃える
// ※ Figma 側が整備されたら、この派生をやめて Figma 値に戻すこと。
//   対象: background.secondary.subtle / background.error.subtle /
//         foreground.semantic.{info,error,warning} / border.semantic.{info,error,warning}
//   (success は Figma に dark 値 #008484 があるためそのまま採用している)
exports.darkColors = {
    background: {
        overlay: '#00000066',
        overlayPrimary: '#1970D433',
        // light の黒 5% と対になる白 5% (暗い面に黒を重ねても見えないため反転)
        overlayHover: '#FFFFFF0D',
        base: '#181F22',
        sub: '#1212128C',
        surface: '#121212CC',
        // 上端スクリムは light の白と対になる黒。不透明度は light と同じ
        topScrimFrom: '#000000A6',
        topScrimTo: '#00000000',
        neutral: { default: '#293840', subtle: '#181F22' },
        // subtle は Figma が light 値 (#E0F7F7) のままなので、info.subtle と
        // 同じ明度帯の teal に落として派生させる
        secondary: { default: '#008484', subtle: '#164D4D' },
        info: { default: '#6CB6FF', subtle: '#16344D' },
        // subtle は Figma が light 値 (#FFCDD2) のままなので派生させる
        error: { default: '#F0808C', subtle: '#3A1620' },
        warning: { default: '#F0A44E', subtle: '#3D2A10' },
        primary: { default: '#459CFF', subtle: '#1341A3' },
    },
    foreground: {
        neutral: {
            primary: '#F1F5F6',
            secondary: '#C1D0D7',
            hint: '#97A7B0',
            disabled: '#798A94',
            white: '#FFFFFF',
            black: '#000000',
        },
        primary: { main: '#459CFF', activated: '#54656D', onPrimary: '#FFFFFF' },
        // info / error / warning は Figma が light 値のままで、暗い面では
        // コントラストが足りない (error #D32F2F は 3.4:1)。
        // background.<同名>.default の dark 値に揃えて派生させる。
        semantic: {
            info: '#6CB6FF',
            error: '#F0808C',
            success: '#008484',
            warning: '#F0A44E',
        },
        filledInk: { light: '#FFFFFF', dark: '#20343F' },
    },
    border: {
        neutral: { default: '#293840', strong: '#54656D' },
        // foreground.semantic と同じ理由で派生させる (Figma は light 値のまま)
        semantic: {
            info: '#6CB6FF',
            error: '#F0808C',
            success: '#008484',
            warning: '#F0A44E',
        },
    },
    unique: { protect: '#1341A3', nft: '#008484', resale: '#459CFF' },
    shadowColor: {
        card: '#FFFFFF0F',
        innerCard: '#00000080',
        brand: '#459CFF52',
    },
};
exports.v2ColorsByMode = {
    light: exports.lightColors,
    dark: exports.darkColors,
};
